import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { T, FONT, MONO, radius, tint, alpha} from './poyais-ui/theme';
import { fmt_k } from './poyais-ui/format';
import { useIsMobile } from './poyais-ui/hooks';
import { Card, Grid, Eyebrow, Badge, Button } from './poyais-ui/primitives';
import { Icon } from './poyais-ui/icons';
import { exportCsv } from './poyais-ui/export';
import { useLang, addTranslations } from './i18n';
import PoyaisSpinner from './PoyaisSpinner';

addTranslations({
  'RTB House Bağlı Değil': 'RTB House Not Connected',
  'Ayarlar sayfasından giriş bilgilerinizi ekleyin.': 'Add your login credentials on the Settings page.',
  'Ayarlara Git': 'Go to Settings',
  'Veri alınamadı': 'Could not load data',
  'Hata': 'Error',
  'TOPLAM HARCAMA': 'TOTAL SPEND', 'DÖNÜŞÜM DEĞERİ': 'CONVERSION VALUE', 'DÖNÜŞÜM': 'CONVERSIONS',
  'GÖSTERİM': 'IMPRESSIONS', 'TIKLAMA': 'CLICKS', 'ORT. CTR': 'AVG. CTR', 'ORT. CPC': 'AVG. CPC',
  'Günlük Trend': 'Daily Trend',
  'Harcama': 'Spend', 'Gösterim': 'Impressions', 'Tıklama': 'Clicks', 'Dönüşüm': 'Conversions',
  'Tarih': 'Date',
  'Kampanyalar': 'Campaigns',
  'kampanya': 'campaigns', 'aktif': 'active', 'Dışa Aktar': 'Export',
  'KAMPANYA': 'CAMPAIGN', 'DURUM': 'STATUS', 'HARCAMA': 'SPEND', 'CONV. DEĞERİ': 'CONV. VALUE',
  'Aktif': 'Active', 'Durduruldu': 'Paused', 'Pasif': 'Inactive',
  'Tümü': 'All',
  'Banner': 'Banner',
  'ürün': 'products',
  'banner': 'banners',
  'gösteriliyor': 'shown',
});

const API = '/api/v1';

function fmtN(v:number){return Number(v).toLocaleString('tr-TR');}
function fmtPct(v:number){return `%${Number(v).toFixed(2)}`;}
function fmtRoas(v:number){return `${Number(v).toFixed(2)}×`;}
function roasColor(r:number){return r>=3?T.greenText:r>=1?T.accent:T.redText;}

function Loader() { return <div className="py-pageload"><PoyaisSpinner size={38} pad={0}/></div>; }

function DisconnectedState(){ const { t } = useLang(); return(<div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:320,gap:14,fontFamily:FONT}}><div style={{fontSize:15,fontWeight:800,color:T.text}}>{t('RTB House Bağlı Değil')}</div><div style={{fontSize:13,color:T.textDim}}>{t('Ayarlar sayfasından giriş bilgilerinizi ekleyin.')}</div><Button variant="solid" color={T.accent} onClick={()=>window.dispatchEvent(new CustomEvent('poyais-nav',{detail:'settings'}))}>{t('Ayarlara Git')}</Button></div>);}

// --- OVERVIEW TAB ---
function OverviewTab({token,start,end}:{token:string;start?:string;end?:string}){
  const { t } = useLang();
  const isMobile=useIsMobile();
  const[data,setData]=useState<any>(null);
  const[loading,setLoading]=useState(true);
  const[error,setError]=useState<string|null>(null);
  const[chartMetric,setChartMetric]=useState<'spend'|'imps'|'clicks'|'conversions'>('spend');
  const today=new Date().toISOString().slice(0,10);
  const monthStart=new Date(new Date().getFullYear(),new Date().getMonth(),1).toISOString().slice(0,10);
  const dateFrom = start || monthStart;
  const dateTo = end || today;
  useEffect(()=>{
    if(!token) return;
    setLoading(true);setError(null);setData(null);
    axios.get(`${API}/rtbhouse/overview`,{params:{token,day_from:dateFrom,day_to:dateTo}})
      .then(r=>{setData(r.data);setLoading(false);})
      .catch(e=>{setError(e.response?.data?.detail||t('Veri alınamadı'));setLoading(false);});
  },[token,dateFrom,dateTo]);
  if(loading)return<Loader/>;
  if(error)return<div style={{background:tint(T.red,8),border:`1px solid ${alpha(T.red, 25)}`,borderRadius:radius.lg,padding:'20px 24px',fontFamily:FONT}}><div style={{fontSize:13.5,fontWeight:800,color:T.redText,marginBottom:6}}>{t('Hata')}</div><div style={{fontSize:12,color:T.textMid}}>{error}</div></div>;
  const trend=data?.daily_trend||[];
  return(
    <div style={{display:'flex',flexDirection:'column',gap:20,fontFamily:FONT}}>
      {/* KPI */}
      <Grid min={isMobile?130:150} gap={isMobile?8:14}>
        {[
          {label:t('TOPLAM HARCAMA'),value:fmt_k(data?.total_spend||0),color:T.accent},
          {label:'ROAS',value:fmtRoas(data?.roas||0),color:roasColor(data?.roas||0)},
          {label:t('DÖNÜŞÜM DEĞERİ'),value:fmt_k(data?.total_conv_value||0),color:T.greenText},
          {label:t('DÖNÜŞÜM'),value:fmtN(data?.total_conversions||0),color:T.blue},
          {label:t('GÖSTERİM'),value:fmtN(data?.total_imps||0),color:T.textDim},
          {label:t('TIKLAMA'),value:fmtN(data?.total_clicks||0),color:T.textDim},
          {label:t('ORT. CTR'),value:fmtPct(data?.avg_ctr||0),color:T.textDim},
          {label:t('ORT. CPC'),value:fmt_k(data?.avg_cpc||0),color:T.textDim},
        ].map((k,i)=>(
          <Card key={i} pad={18}>
            <Eyebrow color={k.color}>{k.label}</Eyebrow>
            <div style={{fontSize:22,fontWeight:800,color:T.text,fontFamily:MONO,fontVariantNumeric:'tabular-nums',marginTop:8}}>{k.value}</div>
          </Card>
        ))}
      </Grid>
      {/* Chart */}
      {trend.length>0&&(
        <Card pad={20}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:700,color:T.text}}>{t('Günlük Trend')}</div>
            <div style={{display:'flex',gap:6}}>
              {(['spend','imps','clicks','conversions'] as const).map(k=>(
                <Button key={k} variant={chartMetric===k?'soft':'ghost'} color={T.accent} onClick={()=>setChartMetric(k)}
                  style={{padding:'4px 12px',fontSize:10,fontWeight:chartMetric===k?700:400}}>
                  {k==='spend'?t('Harcama'):k==='imps'?t('Gösterim'):k==='clicks'?t('Tıklama'):t('Dönüşüm')}
                </Button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trend} margin={{top:4,right:4,left:-20,bottom:0}}>
              <XAxis dataKey="day" tick={{fontSize:9,fill:T.textDim}} tickFormatter={v=>v.slice(5)} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:9,fill:T.textDim}} axisLine={false} tickLine={false} tickFormatter={v=>v>=1000?`${(v/1000).toFixed(0)}K`:v}/>
              <Tooltip formatter={(v:any)=>chartMetric==='spend'?fmt_k(Number(v)):fmtN(Number(v))} labelFormatter={l=>`${t('Tarih')}: ${l}`}/>
              <Line type="monotone" dataKey={chartMetric} stroke={T.accent} strokeWidth={2} dot={false}/>
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
}

// --- CAMPAIGNS TAB ---
function CampaignsTab({token,start,end}:{token:string;start?:string;end?:string}){
  const { t } = useLang();
  const isMobile=useIsMobile();
  const[data,setData]=useState<any[]>([]);
  const[loading,setLoading]=useState(true);
  const today=new Date().toISOString().slice(0,10);
  const monthStart=new Date(new Date().getFullYear(),new Date().getMonth(),1).toISOString().slice(0,10);
  useEffect(()=>{
    setLoading(true);
    axios.get(`${API}/rtbhouse/campaigns`,{params:{token,day_from:start||monthStart,day_to:end||today}})
      .then(r=>{setData(r.data.campaigns||[]);setLoading(false);})
      .catch(()=>setLoading(false));
  },[token,start,end]);
  if(loading)return<Loader/>;
  const statusBadge=(s:string)=>{
    if(s==='ACTIVE')return{text:t('Aktif'),color:T.greenText};
    if(s==='PAUSED')return{text:t('Durduruldu'),color:T.amberText};
    return{text:s||'—',color:T.textDim};
  };
  const handleExport = () => {
    exportCsv(`rtbhouse-kampanyalar-${start || ''}-${end || ''}.csv`, data, [
      { key: 'name', label: t('KAMPANYA') },
      { key: 'status', label: t('DURUM') },
      { key: 'spend', label: t('HARCAMA') },
      { key: 'imps', label: t('GÖSTERİM') },
      { key: 'clicks', label: t('TIKLAMA') },
      { key: 'ctr', label: 'CTR' },
      { key: 'cpc', label: 'CPC' },
      { key: 'conversions', label: t('DÖNÜŞÜM') },
      { key: 'conv_value', label: t('CONV. DEĞERİ') },
      { key: 'roas', label: 'ROAS' },
      { key: 'cpa', label: 'CPA' },
      { key: 'cr', label: 'CR%' },
    ]);
  };

  return(
    <div style={{display:'flex',flexDirection:'column',gap:16,fontFamily:FONT}}>
      <Card pad={0}>
        <div style={{padding:'16px 24px',borderBottom:`1px solid ${T.border}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div style={{fontSize:14,fontWeight:700,color:T.text}}>{t('Kampanyalar')}</div>
          <div style={{display:'flex',alignItems:'center',gap:14}}>
            <div style={{fontSize:11,color:T.textDim}}>{data.length} {t('kampanya')} · {data.filter(c=>c.status==='ACTIVE').length} {t('aktif')}</div>
            <Button variant="ghost" color={T.accent} disabled={data.length===0} onClick={handleExport} style={{padding:'6px 12px',fontSize:11,display:'flex',alignItems:'center',gap:6}}>
              <Icon.download size={13}/>{t('Dışa Aktar')}
            </Button>
          </div>
        </div>
        <div style={{overflowX:'auto' as const}}>
          <table style={{width:'100%',borderCollapse:'collapse' as const,minWidth:900}}>
            <thead style={{borderBottom:`1px solid ${T.border}`,background:T.card}}>
              <tr>
                {['KAMPANYA','DURUM','HARCAMA','GÖSTERİM','TIKLAMA','CTR','CPC','DÖNÜŞÜM','CONV. DEĞERİ','ROAS','CPA','CR%'].map((h,i)=>(
                  <th key={i} style={{padding:'10px 14px',fontSize:9,fontWeight:700,color:T.textDim,textTransform:'uppercase' as const,letterSpacing:'0.08em',textAlign:i===0?'left' as const:'right' as const,whiteSpace:'nowrap' as const}}>{t(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((c:any,i:number)=>{
                const sb=statusBadge(c.status);
                const rc=roasColor(c.roas);
                return(
                  <tr key={i} style={{borderBottom:`1px solid ${T.card}`}} onMouseEnter={e=>(e.currentTarget.style.background=T.card)} onMouseLeave={e=>(e.currentTarget.style.background='transparent')}>
                    <td style={{padding:'12px 14px'}}><div style={{fontSize:12,fontWeight:600,color:T.text,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' as const}}>{c.name}</div><div style={{fontSize:10,color:T.textDim,marginTop:2}}>{c.advertiser}</div></td>
                    <td style={{padding:'12px 14px',textAlign:'right' as const}}><Badge color={sb.color}>{sb.text}</Badge></td>
                    <td style={{padding:'12px 14px',textAlign:'right' as const,fontSize:12,fontWeight:600,color:T.text,fontVariantNumeric:'tabular-nums'}}>{fmt_k(c.spend)}</td>
                    <td style={{padding:'12px 14px',textAlign:'right' as const,fontSize:11,color:T.textMid,fontVariantNumeric:'tabular-nums'}}>{fmtN(c.imps)}</td>
                    <td style={{padding:'12px 14px',textAlign:'right' as const,fontSize:11,color:T.textMid,fontVariantNumeric:'tabular-nums'}}>{fmtN(c.clicks)}</td>
                    <td style={{padding:'12px 14px',textAlign:'right' as const,fontSize:11,color:T.textMid,fontVariantNumeric:'tabular-nums'}}>{fmtPct(c.ctr)}</td>
                    <td style={{padding:'12px 14px',textAlign:'right' as const,fontSize:11,color:T.textMid,fontVariantNumeric:'tabular-nums'}}>{fmt_k(c.cpc)}</td>
                    <td style={{padding:'12px 14px',textAlign:'right' as const,fontSize:12,fontWeight:700,color:T.text,fontVariantNumeric:'tabular-nums'}}>{fmtN(c.conversions)}</td>
                    <td style={{padding:'12px 14px',textAlign:'right' as const,fontSize:12,fontWeight:600,color:T.greenText,fontVariantNumeric:'tabular-nums'}}>{fmt_k(c.conv_value)}</td>
                    <td style={{padding:'12px 14px',textAlign:'right' as const}}><span style={{fontSize:13,fontWeight:700,color:rc}}>{fmtRoas(c.roas)}</span></td>
                    <td style={{padding:'12px 14px',textAlign:'right' as const,fontSize:11,color:T.textMid,fontVariantNumeric:'tabular-nums'}}>{c.cpa>0?fmt_k(c.cpa):'—'}</td>
                    <td style={{padding:'12px 14px',textAlign:'right' as const,fontSize:11,color:T.textMid,fontVariantNumeric:'tabular-nums'}}>{fmtPct(c.cr||0)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// --- CREATIVES TAB ---
function CreativesTab({token}:{token:string}){
  const { t } = useLang();
  const isMobile=useIsMobile();
  const[data,setData]=useState<any[]>([]);
  const[loading,setLoading]=useState(true);
  const[filter,setFilter]=useState('all');
  const[page,setPage]=useState(0);
  const PAGE=20;
  useEffect(()=>{
    setLoading(true);
    axios.get(`${API}/rtbhouse/creatives`,{params:{token}})
      .then(r=>{setData(r.data.creatives||[]);setLoading(false);})
      .catch(()=>setLoading(false));
  },[token]);
  if(loading)return<Loader/>;
  const sizes=Array.from(new Set(data.map((c:any)=>`${c.width}x${c.height}`))).sort();
  const filtered=filter==='all'?data:data.filter((c:any)=>`${c.width}x${c.height}`===filter);
  const totalPages=Math.max(1,Math.ceil(filtered.length/PAGE));
  const paged=filtered.slice(page*PAGE,(page+1)*PAGE);
  return(
    <div style={{display:'flex',flexDirection:'column',gap:16,fontFamily:FONT}}>
      {/* Filtre */}
      <div style={{display:'flex',gap:8,flexWrap:'wrap' as const,alignItems:'center'}}>
        <Button variant={filter==='all'?'soft':'ghost'} color={T.accent} onClick={()=>{setFilter('all');setPage(0);}}
          style={{padding:'5px 14px',fontSize:11,fontWeight:filter==='all'?700:400}}>{t('Tümü')} ({data.length})</Button>
        {sizes.map(s=>(
          <Button key={s} variant={filter===s?'soft':'ghost'} color={T.accent} onClick={()=>{setFilter(s);setPage(0);}}
            style={{padding:'5px 14px',fontSize:11,fontWeight:filter===s?700:400}}>{s}</Button>
        ))}
      </div>
      {/* Grid */}
      <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr 1fr':'repeat(auto-fill,minmax(220px,1fr))',gap:16}}>
        {paged.map((c:any,i:number)=>(
          <Card key={i} pad={0} style={{overflow:'hidden'}}>
            <div style={{background:T.card,display:'flex',alignItems:'center',justifyContent:'center',padding:12,minHeight:120}}>
              <iframe
                src={c.preview_url}
                style={{width:'100%',aspectRatio:`${c.width}/${c.height}`,border:'none',borderRadius:4,pointerEvents:'none',display:'block'}}
                title={c.family}
                loading="lazy"
                sandbox="allow-scripts allow-same-origin"
              />
            </div>
            <div style={{padding:'12px 14px'}}>
              <div style={{fontSize:11,fontWeight:600,color:T.text,marginBottom:4,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' as const}}>{c.family||t('Banner')}</div>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <span style={{fontSize:10,color:T.textDim}}>{c.width}×{c.height}</span>
                <Badge color={c.status==='ACTIVE'?T.greenText:T.textDim}>{c.status==='ACTIVE'?t('Aktif'):t('Pasif')}</Badge>
              </div>
              {c.offers>0&&<div style={{fontSize:10,color:T.accent,marginTop:4}}>{c.offers} {t('ürün')}</div>}
            </div>
          </Card>
        ))}
      </div>
      {/* Pagination */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0'}}>
        <span style={{fontSize:11,color:T.textDim}}>{filtered.length} {t('banner')} · {Math.min(page*PAGE+1,filtered.length)}-{Math.min((page+1)*PAGE,filtered.length)} {t('gösteriliyor')}</span>
        <div style={{display:'flex',gap:4}}>
          <Button variant="ghost" color={T.accent} onClick={()=>setPage(p=>Math.max(0,p-1))} disabled={page===0}
            style={{width:30,height:30,padding:0,fontSize:14,opacity:page===0?.4:1}}>‹</Button>
          {Array.from({length:Math.min(5,totalPages)},(_,i)=>i).map(n=>(
            <Button key={n} variant={page===n?'solid':'ghost'} color={T.accent} onClick={()=>setPage(n)}
              style={{width:30,height:30,padding:0,fontSize:11,fontWeight:700}}>{n+1}</Button>
          ))}
          <Button variant="ghost" color={T.accent} onClick={()=>setPage(p=>Math.min(totalPages-1,p+1))} disabled={page>=totalPages-1}
            style={{width:30,height:30,padding:0,fontSize:14,opacity:page>=totalPages-1?.4:1}}>›</Button>
        </div>
      </div>
    </div>
  );
}

// --- MAIN EXPORT ---
export default function RTBHouseTab({ config, tab='overview', start, end }: { config: any; tab?: string; start?: string; end?: string }) {
  const token = (config?.platforms as any)?.rtbhouse?.token || '';
  const enabled = (config?.platforms as any)?.rtbhouse?.enabled !== false;
  // Config henüz yüklenmemişse bekle
  if (!config?.platforms) return <Loader/>;
  if (!token || !enabled) return <DisconnectedState/>;
  if (tab === 'campaigns') return <CampaignsTab token={token} start={start} end={end}/>;
  if (tab === 'creatives') return <CreativesTab token={token}/>;
  return <OverviewTab token={token} start={start} end={end}/>;
}
